import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { Badge } from '@/components/ui/badge';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { serviceQualityService } from '@/services/providers';
import { showToast } from '@/utils/toast';

interface ServiceQualityManagementProps {
  providerId: number;
  versionId: number;
  regionId: number;
}

interface ServiceQualityData {
  reliability_rating?: number | null;
  customer_satisfaction_score?: number | null;
  setup_difficulty?: string | null;
  customer_support_hours?: string | null;
  support_channels?: any | null;
}

export function ServiceQualityManagement({ 
  providerId, 
  versionId, 
  regionId 
}: ServiceQualityManagementProps) {
  const [data, setData] = useState<ServiceQualityData>({});
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  // Load existing data
  useEffect(() => {
    loadData();
  }, [providerId, versionId, regionId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load existing service quality data
      const qualityData = await serviceQualityService.getServiceQuality(providerId, versionId, regionId);
      if (qualityData) {
        setData(qualityData);
        setSaved(true);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showToast.loadFailed('existing data');
    } finally {
      setLoading(false);
    }
  };

  // Validation function
  const validateServiceQualityData = (data: ServiceQualityData): string[] => {
    const errors: string[] = [];

    // Reliability rating validation (1-5)
    if (data.reliability_rating !== null && data.reliability_rating !== undefined) {
      if (data.reliability_rating < 1 || data.reliability_rating > 5) {
        errors.push("Reliability rating must be between 1 and 5");
      }
    }

    // Customer satisfaction score validation (1-5)
    if (data.customer_satisfaction_score !== null && data.customer_satisfaction_score !== undefined) {
      if (data.customer_satisfaction_score < 1 || data.customer_satisfaction_score > 5) {
        errors.push("Customer satisfaction score must be between 1 and 5");
      }
    }

    // Required fields validation
    if (!data.setup_difficulty) {
      errors.push("Setup difficulty is required");
    }

    if (!data.customer_support_hours) {
      errors.push("Customer support hours is required");
    }

    return errors;
  };

  const handleSave = async () => {
    // Validate data before saving
    const errors = validateServiceQualityData(data);
    if (errors.length > 0) {
      errors.forEach(error => showToast.error(error));
      return;
    }

    setLoading(true);
    try {
      // Use upsert to handle both create and update
      const result = await serviceQualityService.upsertServiceQuality({
        provider_id: providerId,
        version_id: versionId,
        region_id: regionId,
        reliability_rating: data.reliability_rating ?? undefined,
        customer_satisfaction_score: data.customer_satisfaction_score ?? undefined,
        setup_difficulty: data.setup_difficulty as any,
        customer_support_hours: data.customer_support_hours as any,
        support_channels: data.support_channels
      });
      
      console.log('Service quality saved successfully:', result);
      setSaved(true);

      showToast.saved('Service quality data');
    } catch (error) {
      console.error('Error saving service quality:', error);
      showToast.error(error instanceof Error ? error.message : "Failed to save service quality data");
    } finally {
      setLoading(false);
    }
  };

  if (loading && !saved) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Service Quality Management</h2>
          <p className="text-muted-foreground">Configure service quality metrics and support information.</p>
        </div>
        <Badge variant="outline">Provider-wide Only</Badge>
      </div>

      {/* Service Quality Configuration */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Service Quality Configuration</CardTitle>
              <p className="text-sm text-muted-foreground">
                These values apply to all plans for this provider.
              </p>
            </div>
            {saved && (
              <Badge variant="secondary" className="bg-green-100 text-green-800">
                ✓ Saved
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Ratings and Scores */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="reliability_rating">Reliability Rating (1-5)</Label>
                <Input
                  type="number"
                  min="1"
                  max="5"
                  value={data.reliability_rating || ''}
                  onChange={(e) => {
                    const value = e.target.value ? parseInt(e.target.value) : null;
                    // Additional client-side validation
                    if (value !== null && (value < 1 || value > 5)) {
                      showToast.invalid('Reliability rating', 'must be between 1 and 5');
                      return;
                    }
                    setData({ 
                      ...data, 
                      reliability_rating: value 
                    });
                  }}
                  placeholder="e.g., 4"
                />
                <p className="text-xs text-muted-foreground">
                  1 = Poor, 2 = Fair, 3 = Good, 4 = Very Good, 5 = Excellent
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="customer_satisfaction_score">Customer Satisfaction Score (1-5)</Label>
                <Input
                  type="number"
                  min="1"
                  max="5"
                  value={data.customer_satisfaction_score || ''}
                  onChange={(e) => {
                    const value = e.target.value ? parseInt(e.target.value) : null;
                    // Additional client-side validation
                    if (value !== null && (value < 1 || value > 5)) {
                      showToast.invalid('Customer satisfaction score', 'must be between 1 and 5');
                      return;
                    }
                    setData({ 
                      ...data, 
                      customer_satisfaction_score: value 
                    });
                  }}
                  placeholder="e.g., 4"
                />
                <p className="text-xs text-muted-foreground">
                  1 = Very Dissatisfied, 2 = Dissatisfied, 3 = Neutral, 4 = Satisfied, 5 = Very Satisfied
                </p>
              </div>
            </div>

            {/* Setup and Support */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="setup_difficulty">Setup Difficulty</Label>
                <Select
                  value={data.setup_difficulty || ''}
                  onValueChange={(value) => setData({ ...data, setup_difficulty: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select setup difficulty" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self_install">Self Install</SelectItem>
                    <SelectItem value="professional">Professional Required</SelectItem>
                    <SelectItem value="mixed">Mixed (Both Options)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="customer_support_hours">Customer Support Hours</Label>
                <Select
                  value={data.customer_support_hours || ''}
                  onValueChange={(value) => setData({ ...data, customer_support_hours: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select support hours" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="24_7">24/7</SelectItem>
                    <SelectItem value="business_hours">Business Hours</SelectItem>
                    <SelectItem value="limited">Limited Hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-6">
            <Button 
              onClick={handleSave}
              disabled={loading}
              variant={saved ? "outline" : "default"}
            >
              {loading ? 'Saving...' : saved ? 'Update Service Quality' : 'Save Service Quality'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start space-x-3">
            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
            <div>
              <h4 className="font-medium text-sm">Service Quality Information</h4>
              <p className="text-sm text-muted-foreground mt-1">
                Service quality metrics are provider-wide and apply to all plans. These metrics help customers 
                understand the level of service they can expect, including reliability, support quality, and 
                response times. Ratings use a 1-5 scale where 1 is the lowest and 5 is the highest.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}